import { useEffect, useState } from 'react';
import { trackView } from '../utils/analytics';
import { Link, Navigate, useParams } from 'react-router-dom';
import { ArrowRight, MessageCircle } from 'lucide-react';
import { LEGACY_DESTINATION_ROUTES } from '../data/destinations';
import { useCatalog } from '../data/catalog';
import { useMediaOverrides } from '../utils/media';
import { Stay22Block } from '../components/stay22/Stay22Block';

let destSettingsCache = null;
const useDestinationSettings = (slug) => {
  const [all, setAll] = useState(destSettingsCache || []);
  useEffect(() => {
    if (destSettingsCache) return;
    fetch(`${API}/destinations`)
      .then((r) => (r.ok ? r.json() : []))
      .then((d) => { destSettingsCache = Array.isArray(d) ? d : []; setAll(destSettingsCache); })
      .catch(() => {});
  }, []);
  return all.find((d) => d.slug === slug) || null;
};
import { STORIES } from '../components/Destinations';
import { MiniMap } from '../components/MiniMap';
import { waConfigured, waLink } from '../config/whatsapp';
import { useLang } from '../i18n';
import { useSEO } from '../seo';
import { PageHeader } from '../components/PageHeader';
import { DestinationWeather, WEATHER_KEY_OF_ISLAND } from '../components/DestinationWeather';
import { Kicker, Reveal } from '../components/Reveal';
import { MarketplaceCard } from '../components/MarketplaceCard';
import { PartnerCard } from '../components/PartnerCard';
import { EcoAssistContextButton } from '../components/EcoAssistContextButton';
import { useCuratedCopy } from '../data/curatedArchitecture';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STORY_MAP = { 'sao-tome': [0], 'santiago': [1], 'principe': [2] };
const STATUS_STYLE = {
  available: 'bg-eco-green text-white',
  partner_network: 'bg-eco-gold text-eco-deep',
  coming_soon: 'bg-eco-ink/40 text-white',
};

// Translate legacy data-level category strings (stored in EN) — never show raw keys or English on PT pages
const CATEGORY_I18N = {
  Hiking: 'dest.cat.hiking', Culture: 'dest.cat.culture', Sea: 'dest.cat.sea', Gastronomy: 'dest.cat.gastronomy',
  Accommodation: 'nav.stays', Transfers: 'nav.transfers', Experiences: 'nav.experiences', 'Full Packages': 'nav.tours',
  Nature: 'dest.cat.nature', Beaches: 'dest.cat.beaches', Adventure: 'dest.cat.adventure',
  Safari: 'dest.cat.safari', Wildlife: 'dest.cat.wildlife', 'Community Experiences': 'dest.cat.community', Coast: 'dest.cat.coast',
};

const useIslandData = (island, country) => {
  const [partners, setPartners] = useState([]);
  const [guides, setGuides] = useState([]);
  useEffect(() => {
    if (!island) return;
    fetch(`${API}/partners?island=${encodeURIComponent(island)}`)
      .then((r) => r.json())
      .then((d) => setPartners(Array.isArray(d) ? d : []))
      .catch(() => setPartners([]));
    fetch(`${API}/guides`)
      .then((r) => r.json())
      .then((d) => setGuides((Array.isArray(d) ? d : []).filter((g) => g.destination === island || g.destination === country)))
      .catch(() => setGuides([]));
  }, [island, country]);
  return { partners, guides };
};

const CtaRow = ({ planTo, waMsg, exploreTo, stayTo }) => {  const { t, lang } = useLang(); const curated = useCuratedCopy(lang);
  return (
    <div className="mt-8 flex flex-wrap items-center gap-3">
      {exploreTo && (
        <Link to={exploreTo} data-testid="destination-explore-cta" className="group eco-btn-primary">
          {t('nav.explore')} <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
        </Link>
      )}
      {stayTo && (
        <Link to={stayTo} data-testid="destination-stay-cta" className="inline-flex min-h-12 items-center gap-2 rounded-[10px] border border-eco-ink/15 px-6 text-[15px] font-semibold text-eco-ink transition-colors duration-200 hover:border-eco-green hover:text-eco-green md:min-h-11">
          {t('page.accommodations.k')}
        </Link>
      )}
      <Link to={planTo} data-testid="destination-plan-cta" className="inline-flex min-h-12 items-center gap-2 rounded-[10px] bg-eco-gold px-6 text-[15px] font-semibold text-eco-deep transition-colors duration-200 hover:bg-[#b8944f] md:min-h-11">
        {t('nav.plan')}
      </Link>
      <EcoAssistContextButton context="destination">{curated.assist.destination}</EcoAssistContextButton>
      {waConfigured && (
        <a href={waLink(waMsg)} target="_blank" rel="noopener noreferrer" data-testid="destination-whatsapp-cta" className="inline-flex min-h-12 items-center gap-2 rounded-[10px] bg-[#25D366] px-6 text-[15px] font-semibold text-white transition-colors duration-200 hover:bg-[#1eb85a] md:min-h-11">
          <MessageCircle size={15} /> {t('cta.whatsapp')}
        </a>
      )}
    </div>
  );
};

// Real per-destination counts strip — only numbers that come from actual data
const DestinationCounts = ({ dest }) => {
  const { t } = useLang();
  const { experiences, packages, transfers: allTransfers, transfersLoaded } = useCatalog();
  const [stays, setStays] = useState(0);
  const islands = (dest.subs || []).map((s) => s.island);
  useEffect(() => {
    fetch(`${API}/partners?type=accommodation`)
      .then((r) => (r.ok ? r.json() : []))
      .then((d) => setStays((Array.isArray(d) ? d : []).filter((p) => islands.includes(p.island)).length))
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dest.slug]);
  const tours = packages.filter((p) => islands.some((i) => p.destination.includes(i)) || (dest.country === 'ST' && p.destination === 'São Tomé + Príncipe'));
  const exps = experiences.filter((e) => islands.includes(e.island));
  const transfers = allTransfers.filter((tr) => islands.includes(tr.island));
  const items = [
    { label: t('nav.tours'), count: tours.length, to: `/journeys`, testid: 'tours' },
    { label: t('nav.experiences'), count: exps.length, to: islands.length === 1 ? `/experiences?island=${encodeURIComponent(islands[0])}` : '/experiences', testid: 'experiences' },
    { label: t('nav.stays'), count: stays, to: `/accommodations?destination=${encodeURIComponent(dest.name)}`, testid: 'stays' },
    { label: t('nav.transfers'), count: transfers.length, onRequest: transfersLoaded && transfers.length === 0, to: `/transfers?destination=${encodeURIComponent(dest.name)}`, testid: 'transfers' },
  ];
  return (
    <div className="mt-7 grid grid-cols-2 gap-3 sm:grid-cols-4" data-testid="destination-explore-counts">
      {items.map((c) => (
        <Link key={c.testid} to={c.to} data-testid={`destination-count-${c.testid}`} className="rounded-2xl border border-eco-ink/10 bg-eco-cream px-4 py-3.5 text-center transition-colors hover:border-eco-gold/40">
          <span className="eco-number block text-2xl font-bold text-eco-green">{c.count > 0 ? c.count : '—'}</span>
          <span className="mt-0.5 block text-[11px] font-semibold uppercase tracking-[0.12em] text-eco-ink/55">{c.label}</span>
          {c.onRequest && <span className="mt-1 block text-[10px] font-medium text-eco-gold">{t('dest.onrequest')}</span>}
        </Link>
      ))}
    </div>
  );
};

const DestinationExperienceSection = ({ destination, experiences, loaded = true }) => {
  const { t } = useLang();
  const requestTo = `/request-experience?destination=${encodeURIComponent(destination)}`;
  return <section className="bg-eco-cream py-16 md:py-20" data-testid="destination-experience-section"><div className="mx-auto max-w-7xl px-5 md:px-8">
    <Reveal className="flex items-end justify-between gap-4"><div><p className="eco-eyebrow">{t('page.experiences.k')}</p>{loaded && experiences.length === 0 && <h2 className="mt-3 font-serif text-2xl font-semibold text-eco-ink md:text-3xl">{t('destExp.zeroTitle', { destination })}</h2>}</div>{experiences.length > 0 && <Link to={`/experiences?island=${encodeURIComponent(destination)}`} data-testid="destination-view-all-experiences" className="shrink-0 text-sm font-semibold text-eco-gold hover:text-eco-green">{t('view.all')} →</Link>}</Reveal>
    {loaded && experiences.length === 0 ? <Reveal className="mt-7 rounded-3xl border border-eco-ink/10 bg-white p-6 md:p-9" data-testid="destination-experiences-empty-request"><p className="max-w-3xl text-eco-ink/65">{t('destExp.zeroText')}</p><Link to={requestTo} className="eco-btn-primary mt-6 inline-flex" data-testid="destination-request-experience-primary">{t('destExp.cta')} <ArrowRight size={15} /></Link><p className="mt-4 text-xs text-eco-ink/45">{t('destExp.note')}</p></Reveal> : <>
      <div className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">{experiences.map((experience, index) => <Reveal key={experience.slug} delay={(index % 3) * 0.08}><MarketplaceCard experience={experience} to={`/experiences/${experience.slug}`} testid={`destination-exp-${experience.slug}`} image={experience.img} eyebrow={`${experience.island} · ${experience.category}`} title={experience.name} location={experience.location} duration={experience.duration} partner={`${t('detail.operator')}: ${t('destExp.localPartner')}`} cta={t('nav.explore')} /></Reveal>)}</div>
      {loaded && <Reveal className="mt-10 rounded-2xl border border-eco-gold/25 bg-white p-6" data-testid="destination-experiences-secondary-request"><h3 className="text-lg font-semibold text-eco-ink">{t('destExp.secondaryTitle')}</h3><p className="mt-2 text-sm text-eco-ink/60">{t('destExp.secondaryText')}</p><Link to={requestTo} className="eco-btn-secondary mt-5 inline-flex" data-testid="destination-request-experience-secondary">{t('destExp.cta')} <ArrowRight size={15} /></Link></Reveal>}
    </>}
  </div></section>;
};

const IslandView = ({ sub, country, destSlug }) => {
  const { t } = useLang();
  const { experiences, experiencesLoaded, packages, transfers: allTransfers, transfersLoaded } = useCatalog();
  const ov = useMediaOverrides();
  const stay22Settings = useDestinationSettings(destSlug);
  const { partners, guides } = useIslandData(sub.island, country);
  const exps = experiences.filter((e) => e.island === sub.island);
  const stays = partners.filter((p) => p.type === 'accommodation');
  const stories = (STORY_MAP[sub.slug] || []).map((i) => STORIES[i]).filter(Boolean);
  const mapItems = [...exps.map((e) => ({ ...e, kind: 'exp' })), ...partners.map((p) => ({ ...p, kind: 'partner' }))];
  const islandTours = packages.filter((p) => p.destination.includes(sub.island) || p.destination === 'São Tomé + Príncipe' && country === 'ST');
  const islandTransfers = allTransfers.filter((tr) => tr.island === sub.island);
  const exploreCounts = [
    { label: t('nav.tours'), count: islandTours.length, to: `/journeys?destination=${encodeURIComponent(sub.island)}`, testid: 'tours' },
    { label: t('nav.experiences'), count: exps.length, to: `/experiences?island=${encodeURIComponent(sub.island)}`, testid: 'experiences' },
    { label: t('nav.stays'), count: stays.length, to: `/accommodations?destination=${encodeURIComponent(sub.island)}`, testid: 'stays' },
    { label: t('nav.transfers'), count: islandTransfers.length, onRequest: transfersLoaded && islandTransfers.length === 0, to: `/transfers?destination=${encodeURIComponent(sub.island)}`, testid: 'transfers' },
  ];

  return (
    <>
      <section className="bg-white py-16 md:py-20">
        <div className="eco-reading px-5 md:px-8">
          <Reveal>
            <p className="eco-body text-eco-ink/70">{sub.desc}</p>
            {WEATHER_KEY_OF_ISLAND[sub.island] && (
              <DestinationWeather locationKey={WEATHER_KEY_OF_ISLAND[sub.island]} testid={`destination-weather-${sub.slug}`} className="mt-6" />
            )}
            <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-4" data-testid="destination-explore-counts">
              {exploreCounts.map((c) => (
                <Link key={c.testid} to={c.to} data-testid={`destination-count-${c.testid}`} className="rounded-2xl border border-eco-ink/10 bg-eco-cream px-4 py-3.5 text-center transition-colors hover:border-eco-gold/40">
                  <span className="eco-number block text-2xl font-bold text-eco-green">{c.count > 0 ? c.count : '—'}</span>
                  <span className="mt-0.5 block text-[11px] font-semibold uppercase tracking-[0.12em] text-eco-ink/55">{c.label}</span>
                  {c.onRequest && <span className="mt-1 block text-[10px] font-medium text-eco-gold">{t('dest.onrequest')}</span>}
                </Link>
              ))}
            </div>
            <CtaRow
              planTo={`/plan-my-trip?destination=${encodeURIComponent(sub.name)}`}
              waMsg={t('wa.dest', { dest: sub.name })}
              exploreTo={`/experiences?island=${encodeURIComponent(sub.island)}`}
              stayTo={`/accommodations?destination=${encodeURIComponent(sub.island)}`}
            />
          </Reveal>
        </div>
      </section>

      <DestinationExperienceSection destination={sub.island} experiences={exps} loaded={experiencesLoaded} />

      <section className="bg-white py-16 md:py-20">
        <div className="mx-auto max-w-7xl px-5 md:px-8">
          <Reveal className="flex items-end justify-between">
            <h2 className="font-serif text-2xl font-semibold text-eco-ink md:text-3xl">{t('page.accommodations.k')}</h2>
            <Link to={`/accommodations?destination=${encodeURIComponent(sub.island)}`} data-testid="destination-view-all-stays" className="text-sm font-semibold text-eco-gold hover:text-eco-green">{t('view.all')} →</Link>
          </Reveal>
          <div className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {stays.map((p) => (
              <Reveal key={p.id}>
                <PartnerCard
                  partner={p}
                  typeLabel={t('type.accommodation')}
                  testid={`destination-stay-${p.id}`}
                  showPrice
                  cta={t('nav.explore')}
                />
              </Reveal>
            ))}
            {stays.length === 0 && (
              <div data-testid="destination-stays-developing" className="col-span-full rounded-3xl border border-dashed border-eco-ink/15 py-10 text-center">
                <p className="text-sm text-eco-ink/50">{t('partners.developing')}</p>
                <Link to={`/plan-my-trip?destination=${encodeURIComponent(sub.island)}`} data-testid="destination-stays-plan-cta" className="eco-btn-text mt-3 inline-flex justify-center">{t('nav.plan')} <ArrowRight size={14} /></Link>
              </div>
            )}
          </div>
        </div>
      </section>

      {stay22Settings?.stay22_enabled && stay22Settings?.ext_accommodation_enabled && (
        <section className="bg-white py-16 md:py-20" data-testid="destination-stay22-section">
          <div className="mx-auto max-w-3xl px-5 md:px-8">
            <Reveal>
              <Stay22Block
                testid="destination-stay22"
                destination={sub.island}
                lat={stay22Settings.stay22_lat ?? null}
                lng={stay22Settings.stay22_lng ?? null}
                campaign={`eco_dest_${sub.slug.replace(/-/g, '_')}`}
                embedId={stay22Settings.stay22_embed_id || ''}
                showMap={Boolean(stay22Settings.stay22_embed_id) || (destSlug === 'sao-tome-principe' && sub.slug === 'sao-tome')}
              />
            </Reveal>
          </div>
        </section>
      )}

      <section className="bg-eco-cream py-16 md:py-20">
        <div className="mx-auto max-w-7xl px-5 md:px-8">
          <Reveal className="flex items-end justify-between">
            <h2 className="font-serif text-2xl font-semibold text-eco-ink md:text-3xl">{t('partners.kicker')}</h2>
            <Link to="/partners" data-testid="destination-view-all-partners" className="text-sm font-semibold text-eco-gold hover:text-eco-green">{t('view.all')} →</Link>
          </Reveal>
          <div className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {partners.map((p) => (
              <Reveal key={p.id}>
                <PartnerCard
                  partner={p}
                  typeLabel={t(`type.${p.type}`)}
                  testid={`destination-partner-${p.id}`}
                  cta={t('nav.explore')}
                />
              </Reveal>
            ))}
            {partners.length === 0 && (
              <div data-testid="destination-partners-developing" className="col-span-full rounded-3xl border border-dashed border-eco-ink/15 py-10 text-center">
                <p className="text-sm text-eco-ink/50">{t('partners.developing')}</p>
                <Link to={`/plan-my-trip?destination=${encodeURIComponent(sub.island)}`} data-testid="destination-partners-plan-cta" className="eco-btn-text mt-3 inline-flex justify-center">{t('nav.plan')} <ArrowRight size={14} /></Link>
              </div>
            )}
          </div>
        </div>
      </section>

      {stories.length > 0 && (
        <section className="bg-white py-16 md:py-20">
          <div className="mx-auto max-w-7xl px-5 md:px-8">
            <Reveal><Kicker>{t('dest.stories.k')}</Kicker></Reveal>
            <div className="mt-8 grid gap-6 md:grid-cols-2">
              {stories.map((s) => (
                <Reveal key={s.keys}>
                  <Link to={s.to} data-testid={`destination-story-${STORIES.indexOf(s)}`} className="group relative block h-80 overflow-hidden rounded-3xl">
                    <img src={ov[`story:${STORIES.indexOf(s)}`] || s.img} alt={t(`${s.keys}.t`)} loading="lazy" className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-110" />
                    <div className="absolute inset-0 bg-gradient-to-t from-eco-deep/90 via-eco-deep/25 to-eco-deep/10" />
                    <div className="absolute inset-x-0 bottom-0 p-7">
                      <h3 className="font-serif text-2xl font-semibold text-white">{t(`${s.keys}.t`)}</h3>
                      <p className="mt-2 text-sm text-white/70">{t(`${s.keys}.x`)}</p>
                      <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-eco-gold">
                        {t(`${s.keys}.c`)} <ArrowRight size={14} className="transition-transform duration-300 group-hover:translate-x-1" />
                      </span>
                    </div>
                  </Link>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      <section className="bg-eco-cream py-16 md:py-20">
        <div className="mx-auto max-w-7xl px-5 md:px-8">
          <Reveal>
            <Kicker>{t('map.kicker')}</Kicker>
            <h2 className="mt-4 font-serif text-2xl font-semibold text-eco-ink md:text-3xl">{t('map.title')}</h2>
          </Reveal>
          <Reveal delay={0.1} className="mt-8">
            <MiniMap island={sub.island} items={mapItems} />
          </Reveal>
        </div>
      </section>

      {guides.length > 0 && (
        <section className="bg-white py-16 md:py-20">
          <div className="mx-auto max-w-7xl px-5 md:px-8">
            <Reveal className="flex items-end justify-between">
              <h2 className="font-serif text-2xl font-semibold text-eco-ink md:text-3xl">{t('page.guides.k')}</h2>
              <Link to="/travel-guides" data-testid="destination-view-all-guides" className="text-sm font-semibold text-eco-gold hover:text-eco-green">{t('view.all')} →</Link>
            </Reveal>
            <div className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {guides.map((g) => (
                <Reveal key={g.slug}>
                  <Link to={`/travel-guides/${g.slug}`} data-testid={`destination-guide-${g.slug}`} className="group flex h-full flex-col overflow-hidden rounded-3xl bg-eco-cream/60 transition-all duration-500 hover:-translate-y-1 hover:shadow-lg">
                    {g.image && (
                      <div className="h-44 shrink-0 overflow-hidden">
                        <img src={g.image} alt={g.title} loading="lazy" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110" />
                      </div>
                    )}
                    <div className="flex flex-1 flex-col p-5">
                      <h3 className="font-serif text-lg font-semibold text-eco-ink">{g.title}</h3>
                      <p className="mt-2 flex-1 text-sm text-eco-ink/60">{g.excerpt}</p>
                      <span className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-eco-gold">{t('read.more')} <ArrowRight size={14} /></span>
                    </div>
                  </Link>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}
    </>
  );
};

export default function DestinationDetailPage() {
  const { slug, islandSlug } = useParams();
  const { t } = useLang();
  const { destinations, destinationsLoaded, experiences, experiencesLoaded } = useCatalog();
  const dest = destinations.find((d) => d.slug === slug);
  const stay22Cfg = useDestinationSettings(slug);
  const sub = islandSlug ? dest?.subs?.find((s) => s.slug === islandSlug) || null : null;

  useSEO(
    `${sub?.name || dest?.name || 'Destino'} | Eco Collection`,
    (sub?.desc || dest?.positioning || 'Eco Collection').slice(0, 150)
  );

  useEffect(() => {
    if (dest) trackView('destination', sub ? `${slug}/${sub.slug}` : slug);
  }, [dest, sub, slug]);

  if (LEGACY_DESTINATION_ROUTES[slug] && !islandSlug) return <Navigate to={`/destinations/${LEGACY_DESTINATION_ROUTES[slug]}`} replace />;
  // Wait for the visibility map before redirecting — reactivated markets must not bounce on first paint
  if (!dest && !destinationsLoaded) return <div className="min-h-[50vh] bg-eco-cream" data-testid="destination-loading" />;
  if (!dest) return <Navigate to="/destinations" replace />;
  if (islandSlug && !sub) return <Navigate to={`/destinations/${slug}`} replace />;

  // Island page (full content)
  if (sub) {
    return (
      <div data-testid={`destination-page-${slug}-${sub.slug}`}>
        <PageHeader kicker={dest.name} title={sub.name} sub={sub.tags} img={sub.hero} />
        <IslandView sub={sub} country={dest.country} destSlug={dest.slug} />
      </div>
    );
  }

  // Country page with subdestinations (STP, CV)
  if (dest.subs?.length > 0) {
    return (
      <div data-testid={`destination-page-${slug}`}>
        <PageHeader kicker={t(`region.${dest.region}`)} title={dest.name} sub={dest.positioning} img={dest.hero} />
        <section className="bg-white py-16 md:py-20">
          <div className="eco-reading px-5 md:px-8">
            <Reveal>
              <span className={`rounded-full px-4 py-1.5 text-xs font-bold tracking-wide uppercase ${STATUS_STYLE[dest.status]}`}>{t(`dst.${dest.status === 'partner_network' ? 'partner' : dest.status}`)}</span>
              <div className="mt-6 flex flex-wrap justify-center gap-2">
                {dest.categories.map((c) => (
                  <span key={c} className="rounded-full bg-eco-cream px-3.5 py-1.5 text-xs font-medium text-eco-ink/60">{CATEGORY_I18N[c] ? t(CATEGORY_I18N[c]) : c}</span>
                ))}
              </div>
              <DestinationCounts dest={dest} />
              {dest.subs?.[0] && WEATHER_KEY_OF_ISLAND[dest.subs[0].island] && (
                <div className="mt-6 flex justify-center">
                  <DestinationWeather locationKey={WEATHER_KEY_OF_ISLAND[dest.subs[0].island]} testid={`destination-weather-${dest.slug}`} />
                </div>
              )}
              <CtaRow planTo={`/plan-my-trip?destination=${encodeURIComponent(dest.name)}`} waMsg={t('wa.dest', { dest: dest.name })} stayTo={`/accommodations?destination=${encodeURIComponent(dest.name)}`} />
            </Reveal>
          </div>
        </section>
        {stay22Cfg?.stay22_enabled && stay22Cfg?.ext_accommodation_enabled && (
          <section className="bg-white py-16 md:py-20" data-testid="destination-stay22-section">
            <div className="mx-auto max-w-3xl px-5 md:px-8">
              <Reveal>
                <Stay22Block
                  testid="destination-stay22"
                  destination={stay22Cfg.stay22_location || dest.name}
                  lat={stay22Cfg.stay22_lat ?? dest.coords?.[0] ?? null}
                  lng={stay22Cfg.stay22_lng ?? dest.coords?.[1] ?? null}
                  campaign={`eco_dest_${dest.slug.replace(/-/g, '_')}`}
                  showMap={false}
                />
              </Reveal>
            </div>
          </section>
        )}
        <DestinationExperienceSection destination={dest.name} experiences={experiences.filter((experience) => dest.subs.some((subDestination) => subDestination.island === experience.island))} loaded={experiencesLoaded} />
        <section className="bg-eco-cream py-16 md:py-20">
          <div className="mx-auto max-w-6xl px-5 md:px-8">
            <Reveal><Kicker>{t('filter.island')}</Kicker></Reveal>            <div className="mt-8 grid gap-6 md:grid-cols-2">
              {dest.subs.map((s) => (
                <Reveal key={s.slug}>
                  <Link to={`/destinations/${slug}/${s.slug}`} data-testid={`destination-sub-${s.slug}`} className="group relative block h-80 overflow-hidden rounded-3xl">
                    <img src={s.img} alt={s.name} loading="lazy" className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-110" />
                    <div className="absolute inset-0 bg-gradient-to-t from-eco-deep/90 via-eco-deep/25 to-eco-deep/10" />
                    <div className="absolute inset-x-0 bottom-0 p-7">
                      <p className="font-serif text-sm italic text-eco-gold">{s.tagline}</p>
                      <h3 className="mt-1 font-serif text-2xl font-semibold text-white">{s.name}</h3>
                      <span className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-eco-gold">
                        {s.cta} <ArrowRight size={14} className="transition-transform duration-300 group-hover:translate-x-1" />
                      </span>
                    </div>
                  </Link>
                </Reveal>
              ))}
            </div>
            {dest.futureIslands?.length > 0 && (
              <Reveal delay={0.15} className="mt-8 text-center">
                <p className="text-xs font-semibold tracking-[0.25em] uppercase text-eco-ink/40">{t('dst.soon')}</p>
                <div className="mt-3 flex flex-wrap justify-center gap-2">
                  {dest.futureIslands.map((f) => (
                    <span key={f} className="rounded-full border border-dashed border-eco-ink/20 px-4 py-1.5 text-xs text-eco-ink/45">{f}</span>
                  ))}
                </div>
              </Reveal>
            )}
          </div>
        </section>
        <section className="bg-white py-16 md:py-20">
          <div className="mx-auto max-w-7xl px-5 md:px-8">
            <MiniMap island="São Tomé" view={{ center: dest.coords, zoom: 5 }} items={dest.subs.map((s) => ({ name: s.name, lat: s.slug === 'principe' ? 1.64 : s.slug === 'santiago' ? 15.07 : 0.27, lng: s.slug === 'principe' ? 7.41 : s.slug === 'santiago' ? -23.63 : 6.62, kind: 'exp' }))} />
          </div>
        </section>
      </div>
    );
  }

  // New destination (partner network / coming soon) — no fake inventory
  return (
    <div data-testid={`destination-page-${slug}`}>
      <PageHeader kicker={t(`region.${dest.region}`)} title={dest.name} sub={dest.positioning} img={dest.hero} />
      <section className="bg-white py-16 md:py-20">
        <div className="eco-reading px-5 md:px-8">
          <Reveal>
            <span className={`rounded-full px-4 py-1.5 text-xs font-bold tracking-wide uppercase ${STATUS_STYLE[dest.status]}`} data-testid="destination-status">
              {t(`dst.${dest.status === 'partner_network' ? 'partner' : dest.status}`)}
            </span>
            <p className="mt-6 font-serif text-2xl leading-relaxed text-eco-ink md:text-3xl">{dest.message}</p>
            <p className="mt-4 text-sm text-eco-ink/55">{t('empty.dest', { dest: dest.name })}</p>
            <div className="mt-8 flex flex-wrap justify-center gap-2">
              {dest.categories.map((c) => (
                <span key={c} className="rounded-full bg-eco-cream px-3.5 py-1.5 text-xs font-medium text-eco-ink/60">{c}</span>
              ))}
            </div>
            <CtaRow planTo={`/plan-my-trip?destination=${encodeURIComponent(dest.name)}`} waMsg={t('wa.dest', { dest: dest.name })} stayTo={`/accommodations?destination=${encodeURIComponent(dest.name)}`} />
          </Reveal>
        </div>
      </section>
      <DestinationExperienceSection destination={dest.name} experiences={experiences.filter((experience) => experience.destination === dest.name || experience.island === dest.name)} loaded={experiencesLoaded} />
      <section className="bg-eco-cream py-16 md:py-20">
        <div className="mx-auto max-w-5xl px-5 md:px-8">
          <MiniMap island="São Tomé" view={{ center: dest.coords, zoom: 5 }} items={[]} />
        </div>
      </section>
      {stay22Cfg?.stay22_enabled && stay22Cfg?.ext_accommodation_enabled && (
        <section className="bg-white py-16 md:py-20" data-testid="destination-stay22-section">
          <div className="mx-auto max-w-3xl px-5 md:px-8">
            <Reveal>
              <Stay22Block
                testid="destination-stay22"
                destination={stay22Cfg.stay22_location || dest.name}
                lat={stay22Cfg.stay22_lat ?? dest.coords?.[0] ?? null}
                lng={stay22Cfg.stay22_lng ?? dest.coords?.[1] ?? null}
                campaign={`eco_dest_${dest.slug.replace(/-/g, '_')}`}
                embedId={stay22Cfg.stay22_embed_id || ''}
                showMap={Boolean(stay22Cfg.stay22_embed_id)}
              />
            </Reveal>
          </div>
        </section>
      )}
    </div>
  );
}
